import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-3d901df7"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "left-settings-container" }
const _hoisted_3 = { class: "root-and-scale-selector" }
const _hoisted_4 = { class: "select-menus" }
const _hoisted_5 = { class: "markers-toggle" }
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "settings-divider" }, null, -1))
const _hoisted_7 = { class: "right-settings-container" }

import { computed, ref } from "vue";
import { useFretboardStore } from "@/stores/fretboard";
import { storeToRefs } from "pinia";
import KeySelector from "./KeySelector.vue";
import ScaleSelector from "./ScaleSelector.vue";
import SmartToggleSwitch from "./SmartToggleSwitch.vue";
import HighlightSettings from "./HighlightSettings.vue";
import TooltipContainer from "./TooltipContainer.vue";
import { useSimpleTooltip } from "@/composables/useSimpleTooltip";
import ElementTooltip from "./ElementTooltip.vue";
import { useGlobalTooltip } from "@/composables/useGlobalTooltip";
import { onMounted, onUnmounted } from "vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'FretboardSettings',
  setup(__props) {

const store = useFretboardStore();
const { isSettingsCollapsed } = storeToRefs(store);

const isScaleDegree = computed({
  get: () => store.isScaleDegree,
  set: (value: boolean) => store.setIsScaleDegree(value),
});

function toggleSettingsCollapse() {
  store.setSettingsCollapsed(!isSettingsCollapsed.value);
}

// Initialize simple tooltip system (for any remaining simple tooltips)
const { state: tooltipState } = useSimpleTooltip();

// Global tooltip coordination
const {
  registerTooltip,
  unregisterTooltip,
  showTooltip: showGlobalTooltip,
  hideTooltip: hideGlobalTooltip,
} = useGlobalTooltip();

// Element tooltips for positioned tooltips above elements
const rootTooltip = ref({
  visible: false,
  content: "",
  targetElement: null as HTMLElement | null,
});

const scaleTooltip = ref({
  visible: false,
  content: "",
  targetElement: null as HTMLElement | null,
});

// Track if root selector is open to prevent hover events
const isRootSelectorOpen = ref(false);

// Tooltip functions for root note selector
function showRootTooltip(event: MouseEvent) {
  const target = event.currentTarget as HTMLElement;
  rootTooltip.value.content = "Change root";
  rootTooltip.value.targetElement = target;
  rootTooltip.value.visible = true;
  showGlobalTooltip("fretboard-settings-root");
}

function hideRootTooltip() {
  rootTooltip.value.visible = false;
  hideGlobalTooltip("fretboard-settings-root");
}

// New handlers that respect the open state
function handleRootSelectorMouseEnter(event: MouseEvent) {
  // Only show tooltip if selector is not open
  if (!isRootSelectorOpen.value) {
    showRootTooltip(event);
  }
}

function handleRootSelectorMouseLeave() {
  // Only hide tooltip if selector is not open
  if (!isRootSelectorOpen.value) {
    hideRootTooltip();
  }
}

function handleRootSelectorOpened() {
  isRootSelectorOpen.value = true;
  hideRootTooltip(); // Make sure tooltip is hidden
}

function handleRootSelectorClosed() {
  isRootSelectorOpen.value = false;
}

// Tooltip functions for scale selector
function showScaleTooltip(event: MouseEvent) {
  const target = event.currentTarget as HTMLElement;
  scaleTooltip.value.content = "Change scale";
  scaleTooltip.value.targetElement = target;
  scaleTooltip.value.visible = true;
  showGlobalTooltip("fretboard-settings-scale");
}

function hideScaleTooltip() {
  scaleTooltip.value.visible = false;
  hideGlobalTooltip("fretboard-settings-scale");
}

// Register tooltips with global system
onMounted(() => {
  registerTooltip("fretboard-settings-root", hideRootTooltip);
  registerTooltip("fretboard-settings-scale", hideScaleTooltip);
});

onUnmounted(() => {
  unregisterTooltip("fretboard-settings-root");
  unregisterTooltip("fretboard-settings-scale");
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["fretboard-settings", { 'fretboard-settings--collapsed': _unref(isSettingsCollapsed) }])
  }, [
    _createElementVNode("button", {
      class: "collapse-toggle",
      "aria-label": 
        _unref(isSettingsCollapsed) ? 'Expand settings' : 'Collapse settings'
      ,
      onClick: toggleSettingsCollapse
    }, _toDisplayString(_unref(isSettingsCollapsed) ? "+" : "−"), 9, _hoisted_1),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(KeySelector, {
            class: "root-selector",
            onMouseenter: handleRootSelectorMouseEnter,
            onMouseleave: handleRootSelectorMouseLeave,
            onSelectorOpened: handleRootSelectorOpened,
            onSelectorClosed: handleRootSelectorClosed
          }),
          _createVNode(ScaleSelector, {
            class: "scale-selector",
            onMouseenter: showScaleTooltip,
            onMouseleave: hideScaleTooltip
          })
        ])
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(SmartToggleSwitch, {
          modelValue: isScaleDegree.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((isScaleDegree).value = $event)),
          "left-label": "Degrees",
          "right-label": "Notes",
          class: "markers-toggle-switch"
        }, null, 8, ["modelValue"])
      ])
    ]),
    _hoisted_6,
    _createElementVNode("div", _hoisted_7, [
      _createVNode(HighlightSettings)
    ]),
    _createVNode(TooltipContainer, {
      visible: _unref(tooltipState).visible,
      content: _unref(tooltipState).content,
      x: _unref(tooltipState).x,
      y: _unref(tooltipState).y
    }, null, 8, ["visible", "content", "x", "y"]),
    _createVNode(ElementTooltip, {
      visible: rootTooltip.value.visible,
      content: rootTooltip.value.content,
      "target-element": rootTooltip.value.targetElement,
      position: 'above'
    }, null, 8, ["visible", "content", "target-element"]),
    _createVNode(ElementTooltip, {
      visible: scaleTooltip.value.visible,
      content: scaleTooltip.value.content,
      "target-element": scaleTooltip.value.targetElement,
      position: 'above'
    }, null, 8, ["visible", "content", "target-element"])
  ], 2))
}
}

})